<script setup lang="ts">
// Simple debounce utility
function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number,
) {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import type { TextBoxChangeEvent } from '@progress/kendo-vue-inputs'
import PricePerUnitWeight from '../components/PricePerUnitWeight.vue'
import { searchIcon } from '@progress/kendo-svg-icons'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { GetSkuListBySearchWord } from '../api/Sku'
import { useLogger } from 'vue-logger-plugin'
import { FormatCurrency } from '../services/currencyFormatter'
import type {
  IGetSkuListBySearchWordResponse,
  IGetSkuListBySearchWordResponseItem,
} from '../types/SkuTypes'
import {
  GetFullProductImageUrl,
  GetFullProductUrl,
} from '../services/SkuService'
import AoeAddToCartButton from '../components/AoeAddToCartButton.vue'
import AoeAddToWishlistButton from '../components/AoeWishlistImageButton.vue'
import { GetOrderTypeId } from '../stores/SettingsStore'
import type { ComponentPublicInstance } from 'vue'
import { GetAvailableQty } from '../services/InventoryService'
import { trackSearch } from '../analytics/index'

const logger = useLogger()

const show = ref(false)
const searchKeyword = ref('')
const orderTypeId = ref<string>('')

const btnShowSearch = ref<ComponentPublicInstance | null>(null)
const searchBar = ref<HTMLElement | null>(null)

const moreSearchResultsMessage = ref<string | null>(null)
const isShowingMore = ref(false)
const noResultsFound = ref(false)
const hasSearched = ref(false)
const latestSearchRequestId = ref(0)

function invalidatePendingSearches() {
  latestSearchRequestId.value += 1
}

function isLatestRequest(requestId: number, keywordAtRequestTime: string): boolean {
  return (
    requestId === latestSearchRequestId.value &&
    keywordAtRequestTime === searchKeyword.value.trim()
  )
}

interface iViewModel extends IGetSkuListBySearchWordResponseItem {
  fullProductUrl: string
  fullProductImageUrl: string
  calculatedAvailableQty: number
}

const viewModels = ref<Array<iViewModel>>([])

async function UpdateViewModels(
  response: IGetSkuListBySearchWordResponse | null,
  requestId?: number,
  keywordAtRequestTime?: string,
) {
  if (
    requestId !== undefined &&
    keywordAtRequestTime !== undefined &&
    !isLatestRequest(requestId, keywordAtRequestTime)
  ) {
    logger.debug('Ignoring stale view model update before render', requestId)
    return
  }

  if (response) {
    hasSearched.value = true

    if (response.totalCount > 0) {
      noResultsFound.value = false
      const nextViewModels = await Promise.all(
        response.items.map(async item => {
          const fullProductUrl = await GetFullProductUrl(
            item.categoryCode,
            item.product.productCode,
          )
          const fullProductImageUrl = await GetFullProductImageUrl(
            item.product.productCode,
            item.product.attributes.CatalogImage,
          )
          // Calculate available quantity using the InventoryService
          const calculatedAvailableQty = await GetAvailableQty(
            orderTypeId.value,
            item.skuCode,
          )
          logger.debug(
            'Calculated Available Qty for',
            item.skuCode,
            ':',
            calculatedAvailableQty,
          )

          return {
            ...item,
            fullProductUrl,
            fullProductImageUrl,
            calculatedAvailableQty,
          }
        }),
      )

      if (
        requestId !== undefined &&
        keywordAtRequestTime !== undefined &&
        !isLatestRequest(requestId, keywordAtRequestTime)
      ) {
        logger.debug('Ignoring stale view model update after async enrichment', requestId)
        return
      }

      viewModels.value = nextViewModels

      if (response.totalCount > 4) {
        if (isShowingMore.value) {
          moreSearchResultsMessage.value = t('labels.ShowLess')
        } else {
          moreSearchResultsMessage.value =
            t('labels.ShowMore') + ` (${response.totalCount})`
        }
      } else {
        moreSearchResultsMessage.value = null
      }
    } else {
      // No results found
      noResultsFound.value = true
      moreSearchResultsMessage.value = null
      viewModels.value = []
    }
  } else {
    // No search performed or search cleared
    hasSearched.value = false
    noResultsFound.value = false
    moreSearchResultsMessage.value = null
    viewModels.value = []
  }
}

async function runLatestSearch(pageSize: number) {
  const keywordAtRequestTime = searchKeyword.value.trim()
  if (!keywordAtRequestTime) {
    invalidatePendingSearches()
    await UpdateViewModels(null)
    return
  }

  const requestId = ++latestSearchRequestId.value
  const response = await GetSkuListBySearchWord(
    orderTypeId.value,
    keywordAtRequestTime,
    pageSize,
  )

  if (!isLatestRequest(requestId, keywordAtRequestTime)) {
    logger.debug('Ignoring stale search response', requestId)
    return
  }

  await UpdateViewModels(response, requestId, keywordAtRequestTime)

  if (!isLatestRequest(requestId, keywordAtRequestTime)) {
    logger.debug('Ignoring stale analytics tracking for search', requestId)
    return
  }

  trackSearch(keywordAtRequestTime, response?.totalCount ?? 0)
}

function showSearchClickedHandler(event: MouseEvent) {
  logger.debug('showSearchClickedHandler', event, show.value)
  show.value = !show.value
  if (!show.value) {
    invalidatePendingSearches()
    UpdateViewModels(null)
    isShowingMore.value = false
    hasSearched.value = false
    noResultsFound.value = false
  }
}
function searchClickedHandler(event: MouseEvent) {
  logger.debug('searchClickedHandler', event)
  if (!searchKeyword.value || searchKeyword.value.length === 0) {
    invalidatePendingSearches()
    UpdateViewModels(null)
    return
  }
  // Reset to showing fewer results when performing a new search
  isShowingMore.value = false
  void runLatestSearch(4)
}

function showMoreSearchClickedHandler(event: MouseEvent) {
  logger.debug('showMoreSearchClickedHandler', event)
  if (!searchKeyword.value || searchKeyword.value.length === 0) {
    invalidatePendingSearches()
    UpdateViewModels(null)
    return
  }

  if (isShowingMore.value) {
    // If already showing more, revert back to showing less
    isShowingMore.value = false
    void runLatestSearch(4)
  } else {
    // Show more results
    isShowingMore.value = true
    void runLatestSearch(99)
  }
}

function searchKeywordChangedHandler(event: TextBoxChangeEvent) {
  logger.debug('searchKeywordChangedHandler', event)
  searchKeyword.value = event.value as string
}

// Debounced search function
const debouncedSearch = debounce(() => {
  if (!searchKeyword.value || searchKeyword.value.length === 0) {
    invalidatePendingSearches()
    UpdateViewModels(null)
    return
  }
  // Reset to showing fewer results when performing a new search via debounce
  isShowingMore.value = false
  void runLatestSearch(4)
}, 400)

function searchKeywordInputHandler(event: TextBoxChangeEvent) {
  logger.debug('searchKeywordInputHandler', event)
  searchKeyword.value = event.value as string

  invalidatePendingSearches()

  if (!searchKeyword.value || searchKeyword.value.length === 0) {
    UpdateViewModels(null)
    return
  }

  debouncedSearch()
}

function onSearchBoxKeyUp(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    searchClickedHandler(event as unknown as MouseEvent)
  }
}

onMounted(async () => {
  logger.debug('onMounted')

  orderTypeId.value = await GetOrderTypeId()

  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node

  const searchButtonElement = btnShowSearch.value?.$el ?? null
  const searchBarElement = searchBar.value

  if (
    show.value &&
    searchBarElement &&
    !searchBarElement.contains(target) &&
    searchButtonElement &&
    !searchButtonElement.contains(target)
  ) {
    show.value = false
    invalidatePendingSearches()
    UpdateViewModels(null)
    isShowingMore.value = false
    hasSearched.value = false
    noResultsFound.value = false
  }
}
</script>
<template>
  <div class="aoe-search-button" data-testid="search-button-container">
    <KButton
      ref="btnShowSearch"
      class="menuSearchButton"
      :svg-icon="searchIcon"
      :fill-mode="'clear'"
      @click="showSearchClickedHandler($event)"
      data-testid="search-toggle-btn"
    ></KButton>
    <div>
      <span class="ms-2 search-label" data-testid="search-label">{{
        t('labels.Search')
      }}</span>
    </div>
    <Teleport to="#search-bar-placeholder">
      <div
        v-if="show"
        ref="searchBar"
        class="aoe-search-bar"
        data-testid="search-bar"
      >
        <div class="container">
          <div class="row">
            <div
              class="col-12 col-md-2 seachKeyword"
              data-testid="search-keyword-label"
            >
              {{ t('labels.SearchKeyword') }}
            </div>
            <div class="col-12 col-md-7 searchTextBox">
              <KTextBox
                :value="searchKeyword"
                @change="searchKeywordChangedHandler($event)"
                @input="searchKeywordInputHandler($event)"
                @keyup="onSearchBoxKeyUp($event)"
                data-testid="search-input"
              />
            </div>
            <div class="col-10 col-md-2 text-right searchbutton">
              <KButton
                :fill-mode="'outline'"
                @click="searchClickedHandler($event)"
                data-testid="search-submit-btn"
                >{{ t('labels.Search') }}</KButton
              >
            </div>
            <div class="col-2 col-md-1 searchCloseButton">
              <KButton
                :fill-mode="'clear'"
                @click="showSearchClickedHandler($event)"
                class="closeButton"
                data-testid="search-close-btn"
                >&#x2715;</KButton
              >
            </div>
          </div>
          <div
            class="prodSearch row mt-3"
            v-if="hasSearched"
            data-testid="search-results"
          >
            <div
              v-if="noResultsFound"
              class="col-12 no-results-found"
              data-testid="no-results-found"
            >
              <div class="p-4 text-center">
                <p>{{ t('labels.NoResultsFound') }}</p>
              </div>
            </div>
            <div
              v-else
              class="col-12 col-md-6 border"
              v-for="item in viewModels"
              :key="item.skuCode"
              :data-testid="`search-result-item-${item.skuCode}`"
            >
              <div class="row p-2">
                <div class="col-4 imageDiv">
                  <a
                    :href="item.fullProductUrl"
                    :data-category-code="item.product.productCode"
                    :data-testid="`product-link-${item.skuCode}`"
                  >
                    <img
                      :src="item.fullProductImageUrl"
                      class="img-fluid prodImage"
                      :alt="t('cart.ProductImageAlt')"
                      :data-testid="`product-image-${item.skuCode}`"
                    />
                  </a>
                </div>
                <div class="col-8 prodSearchProdDetails p-2">
                  <div class="row prodSearchProdRow">
                    <div
                      class="col-10 prodName p-2"
                      :data-testid="`product-name-${item.skuCode}`"
                    >
                      {{ item.product.productName }}
                    </div>
                    <div class="col-2 searchWishlistBtn p-2">
                      <AoeAddToWishlistButton
                        :skucode="item.skuCode"
                        :data-testid="`wishlist-btn-${item.skuCode}`"
                      ></AoeAddToWishlistButton>
                    </div>
                  </div>
                  <div class="row divProdPrice">
                    <div
                      class="outofstock col-4 p-2"
                      v-if="item.calculatedAvailableQty <= 0"
                      :data-testid="`out-of-stock-${item.skuCode}`"
                    >
                      {{ t('partyOrderEntry.ItemNotAvailable') }}
                    </div>
                    <div
                      v-else
                      class="col-4 prodPrice p-2"
                      :data-testid="`product-price-${item.skuCode}`"
                    >
                      <div v-if="item.salePrice">
                        {{ FormatCurrency(item.salePrice, null) }}
                      </div>
                      <div :class="item.salePrice ? 'prodOriginalPrice' : ''">
                        {{ FormatCurrency(item.retailPrice, null) }}
                      </div>
                    </div>
                    <div
                      class="col-8 prodSearchAddToCartButton x"
                      :value="item.calculatedAvailableQty"
                    >
                      <AoeAddToCartButton
                        :skucode="item.skuCode"
                        :availablequantity="
                          item.calculatedAvailableQty.toString()
                        "
                        :data-testid="`add-to-cart-btn-${item.skuCode}`"
                        :disabled="item.calculatedAvailableQty <= 0"
                      ></AoeAddToCartButton>
                    </div>
                  </div>
                  <div class="row divPricePerUnit">
                    <div class="col-12">
                      <PricePerUnitWeight
                        :RetailPrice="item.retailPrice"
                        :SalePrice="null"
                        :IProductAttributes="item.product.attributes"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="row"
              v-if="moreSearchResultsMessage"
              data-testid="more-results-section"
            >
              <div class="mx-auto moreSearchResults">
                <KButton
                  :fill-mode="'link'"
                  @click="showMoreSearchClickedHandler($event)"
                  data-testid="show-more-results-btn"
                  >{{ moreSearchResultsMessage }}</KButton
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
<style lang="scss">
.aoe-search-button-popup-content {
  background-color: white;
  border: 1px solid black;
  padding: $spacing-2-5;
  width: 66%;
}
.aoe-search-button-popup-content .prodImage {
  width: 100px;
  height: 100px;
}
.aoe-search-button-popup-content .moreSearchResults {
  width: 150px;
}

.aoe-search-button {
  display: flex;
  align-items: center;
}

.aoe-search-bar {
  width: 100%;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  padding: $spacing-4 0;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.prodSearch .prodOriginalPrice {
  @include typography-body;
  font-style: italic;
  font-weight: 600;
  color: var(--vt-c-black);
  text-decoration: line-through;
}

.prodSearch .searchWishlistBtn .wishlistBtn.category-detail-wishlist-button svg {
  transform: scale(5)
}

.no-results-found {
  background-color: white;
  border: 1px solid #e0e0e0;
  margin-bottom: 20px;
}

.no-results-found p {
  @include typography-body;
  color: #666;
  margin: $spacing-5 0;
}
</style>
